import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "./authenticatedFetch";

// Types
export interface TelegramChat {
  id: string;
  chatId: number;
  chatType: string;
  chatTitle?: string;
  chatUsername?: string;
  connectorId?: string;
  connectorName?: string;
  verifiedAt: string;
}

interface GeneratePinResponse {
  pin: string;
  expiresAt: string;
  botUsername: string;
  dmLink: string;
  groupLink: string;
}

interface CheckPinResponse {
  verified: boolean;
  chat?: TelegramChat;
}

export interface PaginatedResponse<T> {
  items: T[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

// API response wrapper
interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/**
 * Generate a PIN for B3OS bot verification
 * @param connectorId - Optional connector ID to associate with the PIN
 * @returns PIN details including the 6-digit code and expiration time
 */
export async function generateTelegramPin(connectorId?: string): Promise<GeneratePinResponse> {
  const response = await authenticatedFetch<ApiResponse<GeneratePinResponse>>(`${V2_ENDPOINT}/v1/telegram-bot/pins`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ connectorId: connectorId ?? "" }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to generate PIN: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * Check if a PIN has been verified by a Telegram chat
 * @param pin - The 6-digit PIN to check
 * @returns Verification status and chat details if verified
 */
export async function checkTelegramPinStatus(pin: string): Promise<CheckPinResponse> {
  const response = await authenticatedFetch<ApiResponse<CheckPinResponse>>(
    `${V2_ENDPOINT}/v1/telegram-bot/pins/${pin}/status`,
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to check PIN status: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}

/**
 * List verified Telegram chats for the current organization
 * @param query - Optional search query
 * @param limit - Number of items per page (default: 50)
 * @param offset - Offset for pagination (default: 0)
 * @param connectorId - Optional connector ID to filter chats
 * @returns Paginated list of verified chats
 */
export async function listTelegramChats(
  query?: string,
  limit: number = 50,
  offset: number = 0,
  connectorId?: string,
): Promise<PaginatedResponse<TelegramChat>> {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  params.append("offset", String(offset));
  if (query) {
    params.append("query", query);
  }
  if (connectorId) {
    params.append("connector_id", connectorId);
  }

  const response = await authenticatedFetch<ApiResponse<PaginatedResponse<TelegramChat>>>(
    `${V2_ENDPOINT}/v1/telegram-bot/chats?${params.toString()}`,
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to list chats: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data;
}
